import React from 'react';
import { Link } from 'react-router-dom';
import HubPageLayout from '../components/HubPageLayout';

// CyberFraud Alert Explained (10-28) — what the alert dashboard is, what each
// part of it means, and why the blue highlight is there. Same reading layout
// as MoSAIC PSYOPS Explained: section title left, body right, hairline rule
// between sections, no tabs or images.
//
// Copy from "FraudAlert Explained.docx" (Cary, 10-28), lightly punctuated.

const SECTIONS = [
  {
    title: 'CyberFraud Alert Overview',
    body: [
      'This is an offensive scammer prevention tool. It is a consolidated single-source platform with the primary objective of digital empowerment: it equips you with the information, details and context to avoid being victimized.',
      'International organized crime syndicates are committing sophisticated frauds on an industrial scale, stealing billions from Americans. The annual FBI / IC3 Internet Crime Reports have a recurring theme — there are no effective technology solutions available to prevent being victimized by online scammers.',
      'It is not about technology — it is being ScammerAware.',
    ],
  },
  {
    title: 'Purpose of the CyberFraud Alert dashboard ( CyberFraud Alert Explained, #1 )',
    body: [
      'This threat-intelligence dashboard simplifies being ScammerAware. It consolidates published FBI PSAs, federal agency alerts and open-source reporting into a single, interactive, organized information portal. Simply put, it puts information where you need it — when you need it.',
      'It brings scam reporting into a single simplified threat stream to warn and proactively alert you, so you avoid falling into the clutches of these 21st-century bandits.',
    ],
  },
  {
    title: 'Dashboard Components ( CyberFraud Alert Explained, #2 )',
    intro: 'Key building blocks that help you stay ScammerAware:',
    items: [
      { term: 'FBI / IC3 PSA Alerts', text: 'Critical warnings to the general public about new cyber threats, phishing schemes, identity theft and impersonation scams, with official guidance on how to protect yourself and report incidents.' },
      { term: 'Consumer Alerts ( SSA / IRS / FTC )', text: 'Official U.S. Government notifications issued to inform consumers about fraudulent activities or unsafe products and services. Their primary purpose is to empower consumers with timely information.' },
      { term: 'ScamWire', text: 'Open-source reporting that uses public information to research, analyze and report on scams.' },
      { term: 'Blue highlight', text: 'Improves readability by indicating that the PSA or consumer alert was issued within the previous 30 days.' },
    ],
  },
  {
    title: 'Global Reach ( CyberFraud Alert Explained, #3 )',
    intro: 'Key advantages:',
    items: [
      { term: '1. Centralized insights', text: 'A unified threat window.' },
      { term: '2. Timely insights', text: 'Real-time threat dashboards and visualizations to avoid evolving threat conditions.' },
      { term: '3. Improved situational awareness', text: 'Persistent scammer-risk decisioning and profiling.' },
    ],
  },
];

const Section = ({ section, first }) => (
  <div className={first ? 'py-10' : 'py-10 border-t border-gray-300'}>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
      <h2 className="md:col-span-5 text-2xl xl:text-3xl font-bold text-black leading-tight mb-0">
        {section.title}
      </h2>
      <div className="md:col-span-7">
        {(section.body || []).map((para) => (
          <p key={para.slice(0, 40)} className="text-black text-base leading-relaxed mb-4 last:mb-0">
            {para}
          </p>
        ))}
        {section.intro && <p className="text-black text-base leading-relaxed mb-4">{section.intro}</p>}
        {section.items && (
          <dl className="space-y-4">
            {section.items.map((item) => (
              <div key={item.term}>
                <dt className="font-bold text-black">{item.term}</dt>
                <dd className="text-black text-base leading-relaxed">{item.text}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  </div>
);

const CyberFraudAlertExplainedPage = () => (
  <HubPageLayout>
    {SECTIONS.map((s, i) => (
      <Section key={s.title} section={s} first={i === 0} />
    ))}
    <div className="border-t border-black pt-8 pb-4">
      <p className="text-black text-base">
        <Link to="/dashboard/fraud-alerts" className="text-blue-600 hover:underline">
          Go to the CyberFraud Alert dashboard &rarr;
        </Link>
      </p>
    </div>
  </HubPageLayout>
);

export default CyberFraudAlertExplainedPage;
